import React from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { BranchFormValues } from "src/models/Branch.model";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import useCountries from "src/hooks/useCountries";
import useCountryStates from "src/hooks/useCountryStates";
import useStateCities from "src/hooks/useStateCities";

type Props = {
  formikProps: FormikProps<BranchFormValues>;
  onClose: () => void;
  formType?: "ADD" | "EDIT";
};

const BranchForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const { countries, isDataLoading: isCountriesLoading } = useCountries();
  const { countryStates, isDataLoading: isCountryStatesLoading } =
    useCountryStates(values?.country?._id || "");
  const { stateCities, isDataLoading: isStateCitiesLoading } = useStateCities(
    values?.state?._id || ""
  );

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {" "}
          {formType === "EDIT" ? "Update Branch" : "Add Branch"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {/* Branch Name */}
        <div className="">
          <ATMTextField
            name="branchName"
            value={values.branchName}
            onChange={(e) => setFieldValue("branchName", e.target.value)}
            label="Branch Name"
            placeholder="Enter branch name"
            onBlur={handleBlur}
          />
        </div>

        {/* Branch Code */}
        <div className="">
          <ATMTextField
            name="branchCode"
            value={values.branchCode}
            onChange={(e) => setFieldValue("branchCode", e.target.value)}
            label="Branch Code"
            placeholder="Enter branch code"
            onBlur={handleBlur}
          />
        </div>

        {/* Branch Address */}
        <div className="">
          <ATMTextField
            name="branchAddress"
            value={values.branchAddress}
            onChange={(e) => setFieldValue("branchAddress", e.target.value)}
            label="Branch Address"
            placeholder="Enter Branch Address"
            onBlur={handleBlur}
          />
        </div>

        {/* Country */}
        <div className="">
          <ATMSelect
            name="country"
            value={values.country}
            onChange={(newValue) => {
              setFieldValue("country", newValue);
              setFieldValue("state", "");
              setFieldValue("city", "");
            }}
            label="Country"
            placeholder="Select Country"
            options={countries}
            isLoading={isCountriesLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return (
                  <div className="capitalize"> {option?.countryName} </div>
                );
              } else {
                return (
                  <div className="capitalize"> {option?.countryName} </div>
                );
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.countryName?.includes(searchValue)
                : true;
            }}
          />
        </div>

        {/* State */}
        <div className="">
          <ATMSelect
            name="state"
            value={values.state}
            onChange={(newValue) => {
              setFieldValue("state", newValue);
              setFieldValue("city", "");
            }}
            label="State"
            placeholder="Select State"
            options={countryStates}
            isLoading={isCountryStatesLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div className="capitalize"> {option?.stateName} </div>;
              } else {
                return <div className="capitalize"> {option?.stateName} </div>;
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.stateName?.includes(searchValue)
                : true;
            }}
            disabled={!values?.country}
          />
        </div>

        {/* City */}
        <div className="">
          <ATMSelect
            name="city"
            value={values.city}
            onChange={(newValue) => {
              setFieldValue("city", newValue);
            }}
            label="City"
            placeholder="Select City"
            options={stateCities}
            isLoading={isStateCitiesLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div className="capitalize"> {option?.cityName} </div>;
              } else {
                return <div className="capitalize"> {option?.cityName} </div>;
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.cityName?.includes(searchValue)
                : true;
            }}
            disabled={!values?.state}
          />
        </div>

        {/* Latitude */}
        <div className="">
          <ATMTextField
            name="lat"
            value={values?.lat}
            onChange={(e) => {
              setFieldValue("lat", e.target.value);
            }}
            label="Latitude"
            placeholder="Latitude"
          />
        </div>

        {/* Longitude*/}
        <div className="">
          <ATMTextField
            name="long"
            value={values.long}
            onChange={(e) => {
              setFieldValue("long", e.target.value);
            }}
            label="Longitude"
            placeholder="Longitude"
          />
        </div>
      </div>
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default BranchForm;
